import styles from './styles.module.scss'
import {useEffect, useState} from "react";
import instance from "../../api";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import VideoModal from "../../components/VideoModal";

const WHATSAPP_DISABLED_MESSAGE = 'WhatsApp is not enabled for this branch'

const isWhatsappDisabledError = (err) => {
  const message = err?.response?.data?.message || err?.response?.data?.error || ''
  return err?.response?.status === 400 && message === WHATSAPP_DISABLED_MESSAGE
}

const News = () => {
  const [news, setNews] = useState([])
  const [logs, setLogs] = useState([])
  const [whatsAppStatus, setWhatsAppStatus] = useState(null)
  const [wabaStatus, setWabaStatus] = useState(null)

  const currentBranch = useSelector(state => state.branchReducer)
  const {t} = useTranslation();

  useEffect(() => {
    let isMounted = true;

    instance().get('/news').then(res => {
      if (isMounted) {
        setNews(res?.data?.data || [])
      }
    }).catch(() => {
      // Handle error silently
    })

    if (currentBranch?.info?.id) {
      instance().get(`/branches/${currentBranch.info.id}/configuration/logs`).then(res => {
        if (isMounted) {
          setLogs(res?.data?.data || [])
        }
      }).catch(() => {
        // Handle error silently
      })

      instance().get(`/branches/${currentBranch.info.id}/whatsapp/status`)
        .then(res => {
          if (isMounted) {
            setWhatsAppStatus(res?.data?.data?.stateInstance === 'authorized' ? 'CORRECT' : 'INCORRECT')
          }
        })
        .catch((err) => {
          if (isMounted) {
            const status = err?.response?.status
            setWhatsAppStatus(status >= 500 || isWhatsappDisabledError(err) ? 'INCORRECT' : 'NA')
          }
        })

      instance().get(`/branches/${currentBranch.info.id}/whatsapp/waba-status`)
        .then(res => {
          if (isMounted) {
            setWabaStatus(res?.data?.data || null)
          }
        })
        .catch(() => {
          if (isMounted) {
            setWabaStatus(null)
          }
        })
    }

    return () => {
      isMounted = false;
    }
  }, [currentBranch?.info?.id]);

  return (
    <div className={styles.container}>
      {/* Platform News Section */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h1 className={styles.sectionTitle}>{t('news') || 'Новости платформы'}</h1>
          <div className={styles.headerSubtitle}>Обновления и новые возможности</div>
        </div>
        
        <div className={styles.newsGrid}>
          {news.length > 0 ? news.map(item => (
            <div className={styles.newsCard} key={item.id}>
              <div className={styles.newsCardHeader}>
                <div className={styles.newsTime}>
                  {(() => {
                    const date = new Date(item.news_time);
                    const day = date.getDate().toString().padStart(2, '0');
                    const month = (date.getMonth() + 1).toString().padStart(2, '0');
                    const year = date.getFullYear();
                    const hours = date.getHours().toString().padStart(2, '0');
                    const minutes = date.getMinutes().toString().padStart(2, '0');
                    return `${day}.${month}.${year}, ${hours}:${minutes}`;
                  })()}
                </div>
              </div>
              <div className={styles.newsContent}>
                <h3 className={styles.newsTitle}>{item.title}</h3>
                <p className={styles.newsDescription}>{item.description}</p>
              </div>
              <div className={styles.newsFooter}>
                {(() => {
                  const newsDate = new Date(item.news_time);
                  const now = new Date();
                  const threeDaysAgo = new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000);
                  return newsDate > threeDaysAgo ? <span className={styles.newsBadge}>Новое</span> : null;
                })()}
                {item.youtube_url && (
                  <VideoModal url={item.youtube_url} />
                )}
              </div>
            </div>
          )) : (
            <div className={styles.emptyState}>
              <h3>Пока нет новостей</h3>
              <p>Здесь будут появляться обновления платформы</p>
            </div>
          )}
        </div>
      </div>

      {/* Business Notifications Section */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h1 className={styles.sectionTitle}>{t('notifications') || 'Уведомления'}</h1>
          <div className={styles.headerSubtitle}>События вашего бизнеса</div>
        </div>

        <div className={styles.notificationsList}>
          {whatsAppStatus === 'INCORRECT' && (
            <div className={`${styles.notificationCard} ${styles.warning}`}>
              <div className={styles.notificationContent}>
                <h4 className={styles.notificationTitle}>Проблема с WhatsApp</h4>
                <p className={styles.notificationText}>
                  {t('disconnectWhatsappInfo') || 'WhatsApp не подключен или отключен'}
                </p>
              </div>
              <div className={styles.notificationTime}>Сейчас</div>
            </div>
          )}

          {whatsAppStatus === 'CORRECT' && (
            <div className={`${styles.notificationCard} ${styles.success}`}>
              <div className={styles.notificationContent}>
                <h4 className={styles.notificationTitle}>WhatsApp подключен</h4>
                <p className={styles.notificationText}>Все уведомления доставляются</p>
              </div>
              <div className={styles.notificationTime}>Активно</div>
            </div>
          )}

          {wabaStatus?.configured && (
            <div className={`${styles.notificationCard} ${wabaStatus.connected ? styles.success : styles.warning}`}>
              <div className={styles.notificationContent}>
                <h4 className={styles.notificationTitle}>
                  {wabaStatus.connected ? (t('wabaConnected') || 'WhatsApp Business подключен') : (t('wabaProblem') || 'Проблема с WhatsApp Business')}
                </h4>
                <p className={styles.notificationText}>
                  {wabaStatus.connected ? (t('wabaConnectedInfo') || 'Уведомления через WABA доставляются') : (t('wabaProblemInfo') || 'WhatsApp Business (WABA) не подключен')}
                </p>
              </div>
              <div className={styles.notificationTime}>{wabaStatus.connected ? 'Активно' : 'Сейчас'}</div>
            </div>
          )}

          {logs.map(item => (
            <div className={styles.notificationCard} key={item.id}>
              <div className={styles.notificationContent}>
                <h4 className={styles.notificationTitle}>{t(`LogTitles.${item.change?.title}`, item.change?.title)}</h4>
                <p className={styles.notificationDetail}>
                  Телефон: {item.user_phone}
                </p>
                <p className={styles.notificationAgent}>{item.user_agent}</p>
              </div>
              <div className={styles.notificationTime}>
                {(() => {
                  const date = new Date(item.created_at);
                  const day = date.getDate().toString().padStart(2, '0');
                  const month = (date.getMonth() + 1).toString().padStart(2, '0');
                  const year = date.getFullYear().toString().slice(-2);
                  const hours = date.getHours().toString().padStart(2, '0');
                  const minutes = date.getMinutes().toString().padStart(2, '0');
                  return `${day}.${month}.${year}, ${hours}:${minutes}`;
                })()}
              </div>
            </div>
          ))}

          {logs.length === 0 && whatsAppStatus !== 'INCORRECT' && !wabaStatus?.configured && (
            <div className={styles.emptyState}>
              <h3>Нет уведомлений</h3>
              <p>Здесь будут отображаться события бизнеса</p>
            </div>
          )}
        </div>
      </div>


    </div>
  )
}

export default News
